import { useEffect, useState, useContext, useMemo } from 'react';
import { CommonDataContext } from '../../firebase/CommonDataContext';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { UserDataContext } from '../../firebase/UserDataContext';
import * as ROUTES from '../../routes/routes';
import { Box, CircularProgress, TextField, Tooltip, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { CheckBox, Clear } from '@mui/icons-material';
import { GlobalSettingsContext } from '../../firebase/GlobalSettingsContext';
import { useCookies } from 'react-cookie';
import { onValue } from '@firebase/database';
import { useDebounce } from 'use-debounce';
import EventSection from './EventSection';
import PropTypes from 'prop-types';

// Helper to build links for scoring actions
function buildScoringLinks({ entry, competition, event, team }) {
    const result = { editHref: null, viewHref: null, isEntered: !!entry?.entered };

    if (result.isEntered && entry?.score?.uid) {
        result.editHref = ROUTES.routeReplace(ROUTES.SCORING_EDIT, {
            editScore: entry.score.uid,
            competition
        });

        result.viewHref = ROUTES.routeReplace(ROUTES.SCORING_VIEW, {
            scoreUid: entry.score.uid,
            competition
        });
    } else {
        // not entered yet
        result.editHref = ROUTES.routeReplace(ROUTES.SCORING_FOR_EVENT_FOR_TEAM, {
            eventUid: event.eventUid,
            teamUid: team.teamUid
        });
    }

    return result;
}

const EventActionCell = (params) => {
    const { entry, competition, event, team } = params.value || {};
    const links = buildScoringLinks({ entry, competition, event, team });

    return (
        <>
            <a href={links.editHref} target="_blank" rel="noopener noreferrer" style={{ marginRight: 10 }}>
                {links.isEntered ? 'edit score' : 'enter new score'}
            </a>
            {links.isEntered && links.viewHref && (
                <a href={links.viewHref} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
                    view score
                </a>
            )}
        </>
    );
};

const eventColumns = [
    { field: 'col1', headerName: 'Team Number ', width: 150, sortComparator: (v1, v2) => Number(v1) - Number(v2) },
    { field: 'col2', headerName: 'Team Name ', width: 300 },
    { field: 'col3', headerName: 'Category', width: 150 },
    {
        field: 'col4', headerName: 'Has been entered', width: 150,
        renderCell: (params) => (params.value ? <CheckBox /> : <Clear />)
    },
    { field: 'col5', headerName: 'Final Score', width: 150, getApplyQuickFilterFn: () => null },
    { field: 'col6', headerName: 'Action', width: 250, renderCell: EventActionCell }
];

const ScoreCommitteeDashboardV2 = (props) => {
    // simple state replaces the previous reducer for clarity
    const [eventFocus] = useState(true);
    const [eventSearch, setEventSearch] = useState('');
    const [teamSearch, setTeamSearch] = useState('');
    const [cookies, setCookie] = useCookies(['previousSearch']);

    const { firebase } = useContext(FirebaseNewContext);
    const { globalSettings } = useContext(GlobalSettingsContext);
    const { defaultSelectedCompetition } = useContext(UserDataContext);
    const { eventsMap: globalEventsMap, teamsMap: globalTeamsMap } = useContext(CommonDataContext);

    const [debouncedSearch] = useDebounce(eventSearch, 250);

    const eventsMap = useMemo(() => globalEventsMap?.[defaultSelectedCompetition] || {}, [defaultSelectedCompetition, globalEventsMap]);
    const teamsMap = useMemo(() => globalTeamsMap?.[defaultSelectedCompetition] || {}, [defaultSelectedCompetition, globalTeamsMap]);
    const categoriesMap = useMemo(() => (globalSettings?.categories?.[defaultSelectedCompetition]) || {}, [defaultSelectedCompetition, globalSettings]);

    const [scoresMap, setScoresMap] = useState({});

    // Initialize eventSearch from cookie if present (preserve feature)
    useEffect(() => {
        const prev = cookies.previousSearch;
        if (prev && typeof prev === 'string') {
            setEventSearch(prev);
        }
    }, []); // run once on mount

    // Listen for scores (prefer realtime listener if available)
    useEffect(() => {
        let unsub = null;

        if (typeof firebase?.listenScoresMap === 'function') {
            const ref = firebase.listenScoresMap();
            if (ref) {
                unsub = onValue(ref, (snapshot) => {
                    try {
                        const scoresMapVal = snapshot.val() || {};
                        setScoresMap(scoresMapVal[defaultSelectedCompetition] || {});
                    } catch (e) {
                        console.error('Failed to parse scores snapshot', e);
                        setScoresMap({});
                    }
                });
            }
        } else if (typeof firebase?.getScores === 'function') {
            firebase.getScores(defaultSelectedCompetition)
                .then((result) => setScoresMap(result || {}))
                .catch((e) => {
                    console.error('Failed to fetch scores via getScores', e);
                    setScoresMap({});
                });
        } else {
            console.warn('firebase.listenScoresMap and firebase.getScores are not available on adapter');
            setScoresMap({});
        }

        return () => {
            if (typeof unsub === 'function') {
                try { unsub(); } catch (e) { /* ignore */ }
            }
        };
    }, [firebase, defaultSelectedCompetition]);

    // percentage done (computed locally from cached maps)
    const [percentageDone, setPercentageDone] = useState(null);
    const [scoreCounts, setScoreCounts] = useState({ recorded: 0, expected: 0 });
    useEffect(() => {
        try {
            const events = eventsMap || {};
            const teams = teamsMap || {};
            const scores = scoresMap || {};

            const teamCount = Object.keys(teams).length;

            // Build expected pairs: all teams × all events (matches missing report logic)
            const expectedPairs = new Set();
            const allTeamKeys = Object.keys(teams);
            Object.keys(events).forEach((eK) => {
                allTeamKeys.forEach((tK) => expectedPairs.add(`${eK}::${tK}`));
            });

            const expected = expectedPairs.size;
            if (expected === 0) {
                setPercentageDone(null);
                return;
            }

            // Build a map of event::team -> score for quick lookup
            // Prefer a "non-missing" score if multiple exist for the same pair
            const scoreByPair = {};
            Object.keys(scores || {}).forEach((sK) => {
                const s = scores[sK] || {};
                const e = s.eventUid || s.event?.eventUid;
                const t = s.teamUid || s.team?.teamUid;
                if (!e || !t) return;
                const key = `${e}::${t}`;
                const existing = scoreByPair[key];
                if (!existing) {
                    scoreByPair[key] = s;
                } else {
                    // Keep the better score: prefer one with finalScore > 0 or scoreMax
                    const existingIsMissing = (Number(existing.finalScore) === 0 && !existing.scoreMax);
                    const newIsMissing = (Number(s.finalScore) === 0 && !s.scoreMax);
                    if (existingIsMissing && !newIsMissing) {
                        scoreByPair[key] = s;
                    }
                }
            });

            // Count missing pairs using the same logic as the missing report:
            // missing = no score exists OR (finalScore === 0 AND !scoreMax)
            let missing = 0;
            expectedPairs.forEach((pair) => {
                const s = scoreByPair[pair];
                if (!s || (Number(s.finalScore) === 0 && !s.scoreMax)) {
                    missing++;
                }
            });

            const recorded = expected - missing;
            const ratio = Math.max(0, Math.min(1, recorded / expected));


            setScoreCounts({ recorded, expected });
            setPercentageDone(ratio);
        } catch (err) {
            console.error('local percentage calc error', err);
            setPercentageDone(null);
        }
    }, [eventsMap, teamsMap, scoresMap]);

    // Build a map of events -> teams with entry info, memoized for performance
    const eventKeysWithTeams = useMemo(() => {
        const MAX_RESULTS = 5;
        const acc = {};

        const eventUids = Object.keys(eventsMap || {});
        for (let i = 0; i < eventUids.length && Object.keys(acc).length < MAX_RESULTS; i++) {
            const eventUid = eventUids[i];
            const event = eventsMap[eventUid];

            if (debouncedSearch) {
                const searchLower = String(debouncedSearch).toLowerCase();
                const eventNumberMatch = String(event?.number) === searchLower;
                const nameMatch = String(event?.name || '').toLowerCase().includes(searchLower);
                if (!eventNumberMatch && !nameMatch) continue;
            }

            // start with deep copy of teamsMap entries for this competition
            const teamUids = Object.keys(teamsMap || {});
            acc[eventUid] = teamUids.reduce((tAcc, tUid) => {
                tAcc[tUid] = { ...teamsMap[tUid], entered: false };
                return tAcc;
            }, {});
        }

        // annotate with scores
        Object.keys(scoresMap || {}).forEach((scoreUid) => {
            const score = { uid: scoreUid, ...scoresMap[scoreUid] };
            if (score.eventUid && acc[score.eventUid] && acc[score.eventUid][score.teamUid]) {
                // Only mark as entered if finalScore is numeric and > 0
                const rawScore = (score.finalScore !== undefined && score.finalScore !== null) ? score.finalScore : (score.score && score.score.finalScore !== undefined ? score.score.finalScore : null);
                const num = rawScore === null || rawScore === '' ? NaN : Number(rawScore);
                if (Number.isFinite(num) && num > 0) {
                    acc[score.eventUid][score.teamUid].entered = true;
                    acc[score.eventUid][score.teamUid].score = score;
                }
            }
        });

        return acc;
    }, [eventsMap, teamsMap, scoresMap, debouncedSearch]);

    // Render
    return (
        <Grid2 container direction="column" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" fontWeight={700}>Dashboard</Typography>
                <Tooltip title={`${scoreCounts.recorded} / ${scoreCounts.expected} scoresheets entered`} arrow>
                    <Box sx={{ position: 'relative', display: 'inline-flex', cursor: 'pointer' }} aria-label="progress">
                        <CircularProgress
                            variant={percentageDone == null ? 'indeterminate' : 'determinate'}
                            value={typeof percentageDone === 'number' ? percentageDone * 100 : undefined}
                            size={48}
                            thickness={6}
                            color="success"
                        />

                        {typeof percentageDone === 'number' && (
                            <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="caption" component="div">{`${percentageDone >= 1 ? 100 : Math.floor(percentageDone * 100)}%`}</Typography>
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            </Box>

            <Grid2 container sx={{ flexDirection: 'column', pb: 2, width: '100%', maxWidth: '100%' }}>
                {eventFocus ? (
                    <TextField
                        fullWidth
                        type="text"
                        label="Search by Events number or name"
                        value={eventSearch}
                        placeholder="Search by event number or name"
                        onChange={(e) => {
                            e.preventDefault();
                            const val = e.target.value;
                            setEventSearch(val);
                            setCookie('previousSearch', val);
                        }}
                    />
                ) : (
                        <TextField
                            fullWidth
                            type="text"
                            label="Search by Team number or name"
                            value={teamSearch}
                            placeholder="Search by team number or name"
                            onChange={(e) => {
                                e.preventDefault();
                                setTeamSearch(e.target.value);
                            }}
                        />
                    )}
            </Grid2>

            {Object.keys(eventKeysWithTeams).map((eKey) => {
                const event = eventsMap[eKey] || {};
                const eventRows = Object.keys(eventKeysWithTeams[eKey] || {}).map((t) => {
                    const team = teamsMap[t] || {};
                    const entry = eventKeysWithTeams[eKey][t] || {};
                    return {
                        id: `${eKey}-${t}`,
                        col1: team?.teamNumber || '',
                        col2: team?.teamName || '',
                        col3: categoriesMap[team?.category] || '',
                        col4: !!entry.entered,
                        col5: entry.entered ? entry.score?.finalScore ?? '-' : '-',
                        col6: { entry, competition: defaultSelectedCompetition, event: { ...event, eventUid: eKey }, team: { ...team, teamUid: t } }
                    };
                });

                return (
                    <EventSection key={eKey} event={event} eventRows={eventRows} eventColumns={eventColumns} />
                );
            })}
        </Grid2>
    );
};

ScoreCommitteeDashboardV2.propTypes = {
    competition: PropTypes.string
};

export default ScoreCommitteeDashboardV2;
